import { IAPIRequestMetaData } from "redux/_core/api/types";
import { getType } from "typesafe-actions";
import toolActions from "./actions";
import {
	IActivityLogFilters,
	ICPActivityLog,
	IConfigInfo,
	IConfigListItem,
	ICopyPayload,
	IPrintInfo,
	IPrintListItem,
	ITenant,
	TConfigType,
	TPrintType,
	IPrintCopyPayload,
	IGroupInfo,
	IIndividualGroup,
} from "./@types";
import { IVessel, IVesselUser, TCount } from "../feedback/@types";
import { commonUtils } from "redux/_common";

const toolAPIs = {
	loadTenantsList(): IAPIRequestMetaData<ITenant[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/tenants`,
			method: "GET",
			feature: getType(toolActions.commands.toolTenantListLoad),
		};
	},
	loadVesselSpecificUsers(
		tenantId?: string,
		vesselId?: string,
		isTrainingRequired?: boolean
	): IAPIRequestMetaData<IVesselUser[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/users`,
			feature: getType(toolActions.commands.toolVesselSpecificUsersLoad),
			method: "GET",
			queryParams: {
				tenantId,
				vesselId,
				isTrainingRequired,
			},
		};
	},
	userTrainingCertificateApprove(userId: string): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/users`,
			feature: getType(toolActions.commands.toolVesselSpecificUsersLoad),
			method: "PATCH",
			queryParams: {
				action: "issueTrainingCertificate",
			},
			body: {
				_id: userId,
			},
		};
	},
	tenantSpecificAddTrainingUsersFromCSV(
		users: IVesselUser[],
		tenantId: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/users`,
			feature: getType(
				toolActions.commands.toolTenantSpecificAddTrainingUsersFromCSV
			),
			method: "POST",
			body: { users, tenantId },
		};
	},
	editVesselSpecificUser(
		userId: string,
		trainingDate: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/users?action=trainingDate`,
			feature: getType(toolActions.commands.toolVesselSpecificUserEdit),
			method: "PATCH",
			body: {
				_id: userId,
				trainingDate,
			},
		};
	},
	loadTenantSpecificVesselsList(
		tenantId: string
	): IAPIRequestMetaData<IVessel[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/vessels`,
			method: "GET",
			feature: getType(toolActions.commands.toolTenantSpecificVesselLoad),
			queryParams: {
				tenantId,
			},
		};
	},
	loadConfigsListForType(
		category: TConfigType,
		userId: string,
		token?: string,
		vesselId?: string
	): IAPIRequestMetaData<IConfigListItem[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "GET",
			feature: getType(toolActions.commands.toolConfigListLoad),
			queryParams: {
				category,
				vesselId: vesselId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	loadConfigInfo(
		configId: string,
		token: string,
		userId: string
	): IAPIRequestMetaData<IConfigInfo> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "GET",
			feature: getType(toolActions.commands.toolConfigLoad),
			queryParams: {
				id: configId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	configCreate(
		config: any,
		token: string,
		userId: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "POST",
			feature: getType(toolActions.commands.toolConfigCreate),
			body: config,
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	configUpdate(
		config: any,
		token: string,
		userId: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "PATCH",
			feature: getType(toolActions.commands.toolConfigUpdate),
			body: config,
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	configCopy(
		payload: ICopyPayload,
		action: "copy",
		token: string,
		userId: string
	): IAPIRequestMetaData<{ message: string }> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "POST",
			feature: getType(toolActions.commands.toolConfigCopy),
			body: payload,
			queryParams: {
				action,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	configDelete(
		configId: string,
		token: string,
		userId: string
	): IAPIRequestMetaData<{ message: string }> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/configs`,
			method: "DELETE",
			feature: getType(toolActions.commands.toolConfigDelete),
			queryParams: {
				id: configId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	loadPrintsListForType(
		category: TPrintType,
		userId: string,
		token?: string,
		vesselId?: string
	): IAPIRequestMetaData<IPrintListItem[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "GET",
			feature: getType(toolActions.commands.toolPrintListLoad),
			queryParams: {
				category,
				vesselId: vesselId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	loadPrintInfo(
		printId: string,
		token: string,
		userId: string
	): IAPIRequestMetaData<IPrintInfo> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "GET",
			feature: getType(toolActions.commands.toolPrintLoad),
			queryParams: {
				id: printId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	printCreate(
		print: any,
		token: string,
		userId: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "POST",
			feature: getType(toolActions.commands.toolPrintCreate),
			body: print,
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	printUpdate(
		print: any,
		token: string,
		userId: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "PATCH",
			feature: getType(toolActions.commands.toolPrintUpdate),
			body: print,
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	printCopy(
		payload: IPrintCopyPayload,
		action: "copy",
		token: string,
		userId: string
	): IAPIRequestMetaData<{ message: string }> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "POST",
			feature: getType(toolActions.commands.toolPrintCopy),
			body: payload,
			queryParams: {
				action,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	printDelete(
		printId: string,
		token: string,
		userId: string
	): IAPIRequestMetaData<{ message: string }> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/prints`,
			method: "DELETE",
			feature: getType(toolActions.commands.toolPrintDelete),
			queryParams: {
				id: printId,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	toolCPActivityLogList(
		token: string,
		userId: string,
		itemsPerPage: number,
		pageNumber: number,
		filters?: IActivityLogFilters
	): IAPIRequestMetaData<ICPActivityLog[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/activitylog`,
			method: "GET",
			feature: getType(toolActions.commands.toolCPActivityLogLoad),
			queryParams: {
				limit: itemsPerPage,
				skip: (pageNumber - 1) * itemsPerPage,
				module: filters?.module,
				userId: filters?.userId,
				vesselId: filters?.vesselId,
				startDate: filters?.startDate,
				endDate: filters?.endDate,
			},
			headers: {
				// @ts-ignore
				"x-token": token,
				"x-user-id": userId,
			},
		};
	},
	getListCountByModelName(
		modelName: string,
		filters?: IActivityLogFilters
	): IAPIRequestMetaData<TCount> {
		const selector: Record<string, any> = {};
		if (filters?.module) selector.module = filters.module;
		if (filters?.userId) selector.userId = filters.userId;
		if (filters?.vesselId) selector.vesselId = filters.vesselId;
		if (filters?.startDate || filters?.endDate) {
			selector.timestamp = {};
			if (filters?.startDate) selector.timestamp.$gte = filters.startDate;
			if (filters?.endDate) selector.timestamp.$lte = filters.endDate;
		}
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/count/`,
			feature: getType(toolActions.commands.toolCountListLoad),
			method: "GET",
			body: {},
			queryParams: {
				modelName,
				filter: Object.keys(selector).length
					? JSON.stringify({ selector })
					: undefined,
			},
		};
	},
	loadGroupsList(tenantId?: string): IAPIRequestMetaData<IGroupInfo[]> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/groups`,
			method: "GET",
			feature: getType(toolActions.commands.toolGroupsListLoad),
			queryParams: {
				tenantId,
			},
		};
	},
	createGroup(groupDetails: IIndividualGroup): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/groups`,
			method: "POST",
			feature: getType(toolActions.commands.toolGroupsGroupCreate),
			body: {
				...groupDetails,
			},
		};
	},
	updateGroup(
		groupDetails: Partial<IIndividualGroup & { groupId: string }>
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/groups`,
			method: "PATCH",
			feature: getType(toolActions.commands.toolGroupsGroupUpdate),
			body: {
				...groupDetails,
			},
		};
	},
	loadReleasesList(
		tenantId?: string,
		page?: number,
		vesselId?: string,
		buildChannel?: string,
		autoUpdate?: string,
		revpos?: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			method: "GET",
			feature: getType(toolActions.commands.toolReleasesListLoad),
			queryParams: {
				tenantId,
				page,
				vesselId,
				buildChannel,
				autoUpdate,
				revpos,
			},
		};
	},
	loadLogsPdfList(
		tenantName: string,
		vesselName: string,
		startDate: string,
		endDate: string,
		code: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getLogsPdfBaseUrl()}/logs-pdf`,
			method: "GET",
			feature: getType(toolActions.commands.toolLogsPdfListLoad),
			queryParams: {
				tenantName,
				vesselName,
				startDate,
				endDate,
				code,
			},
		};
	},
	uploadRelease(formData: FormData): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseUpload),
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			body: formData,
			headers: {
				"Content-Type": "multipart/form-data",
			},
		};
	},
	deployRelease(
		deviceId: string,
		buildChannel: "main" | "hotfix"
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseDeploy),
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "deploy",
			},
			body: {
				deviceId,
				buildChannel,
			},
		};
	},
	deployToMultipleVessels(
		deviceIds: string[],
		buildChannel: "main" | "hotfix"
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseMultiDeploy),
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "deploy-multiple",
			},
			body: {
				deviceIds,
				buildChannel,
			},
		};
	},
	deployToAllVessels(
		tenantId: string,
		buildChannel: "main" | "hotfix"
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseTenantDeploy),
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "deploy-tenant",
			},
			body: {
				tenantId,
				buildChannel,
			},
		};
	},
	loadReleaseHistory(): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseHistoryLoad),
			method: "GET",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "history",
			},
		};
	},
	loadGithubReleases(repo: 'frontend' | 'backend'): IAPIRequestMetaData<any> {
		return {
			method: "GET",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "github-releases",
				repo,
			},
			feature: getType(toolActions.commands.toolReleaseHistoryLoad),
		};
	},
	uploadAutomatedRelease(payload: { frontendReleaseId: number; backendReleaseId: number; buildChannel: 'main' | 'hotfix'; }): IAPIRequestMetaData<any> {
		return {
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "automated-upload",
			},
			body: payload,
			feature: getType(toolActions.commands.toolReleaseUpload),
		};
	},
	summarizeChangelog(changelog: string): IAPIRequestMetaData<{ summary: string }> {
		return {
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: { action: "summarize-changelog" },
			body: { changelog },
			feature: getType(toolActions.commands.toolReleaseUpload),
		};
	},
	loadJobsHistory(
		page: number = 1, 
		limit: number = 10,
		filters?: {
			startDate?: string;
			endDate?: string;
			tenantId?: string;
			vesselId?: string;
			code?: string;
			status?: string;
		}
	): IAPIRequestMetaData<any> {
		const queryParams: any = {
			page,
			limit,
		};
		
		// Add filter parameters if provided
		if (filters) {
			if (filters.startDate) queryParams.startDate = filters.startDate;
			if (filters.endDate) queryParams.endDate = filters.endDate;
			if (filters.tenantId) queryParams.tenantId = filters.tenantId;
			if (filters.vesselId) queryParams.vesselId = filters.vesselId;
			if (filters.code) queryParams.code = filters.code;
			if (filters.status && filters.status !== 'all') queryParams.status = filters.status;
		}

		return {
			feature: getType(toolActions.commands.toolJobsHistoryLoad),
			method: "GET",
			url: `${commonUtils.getLogsPdfBaseUrl()}/job-stats`,
			queryParams,
		};
	},
	loadBullMQJobs(): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolBullMQJobsLoad),
			method: "GET",
			url: `${commonUtils.getLogsPdfBaseUrl()}/all-jobs`,
		};
	},
	loadPdfCronStats(
		page: number = 1,
		limit: number = 10
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolPdfCronLoad),
			method: "GET",
			url: `${commonUtils.getLogsPdfBaseUrl()}/stats/pdf-cron`,
			queryParams: {
				page,
				limit,
			},
		};
	},
	loadFailureCronStats(
		page: number = 1,
		limit: number = 10
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolFailureCronLoad),
			method: "GET",
			url: `${commonUtils.getLogsPdfBaseUrl()}/stats/failure-cron`,
			queryParams: {
				page,
				limit,
			},
		};
	},
	toggleAutoUpdate(
		deviceId: string,
		autoUpdate: boolean
	): IAPIRequestMetaData<any> {
		return {
			feature: getType(toolActions.commands.toolReleaseToggleAutoUpdate),
			method: "POST",
			url: `${commonUtils.getAPIBaseUrl()}/api/releases`,
			queryParams: {
				action: "toggle-auto-update",
			},
			body: {
				deviceId,
				autoUpdate,
			},
		};
	},
	loadIdlEvents(
		tenantId: string,
		vesselId: string,
		startDate: string,
		endDate: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/idl-events`,
			method: "GET",
			feature: getType(toolActions.commands.toolIdlEventsLoad),
			queryParams: {
				tenantId,
				vesselId,
				startDate,
				endDate,
			},
		};
	},
	createIdlEvent(
		date: string,
		status: "SKIPPED" | "REPEATED",
		remarks: string,
		vesselId: string,
		tenantId: string,
		vesselName?: string
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/idl-events`,
			method: "POST",
			feature: getType(toolActions.commands.toolIdlEventCreate),
			body: {
				date,
				status,
				remarks,
				vesselId,
				tenantId,
				vesselName,
			},
		};
	},
	deleteIdlEvent(eventId: string): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/idl-events`,
			method: "DELETE",
			feature: getType(toolActions.commands.toolIdlEventDelete),
			queryParams: {
				id: eventId,
			},
		};
	},
};

export default toolAPIs;
